import * as React from 'react';
import { Outlet } from 'react-router-dom';

import ConfigProvider from 'antd/es/config-provider';
import { shallowEqual, useSelector } from 'react-redux';

import loadable from '@common/react/loadable/loadableSettings';
import '@common/react/yupLocaleSettings';
import { LoaderProvider } from '@common/react/components/Core/LoaderProvider/LoaderProvider';
import ErrorBoundaryWithSentry from '@common/react/components/UI/ErrorBoundaryWithSentry/ErrorBoundaryWithSentry';
import { ChatSettingsProvider } from '@common/react/components/Chat/ChatSettingsProvider';
import { RequestProvider } from '@common/react/components/RequestProvider/RequestProvider';
// import { ReactionsPlugin } from '@common/react/components/Chat/Reactions/ReactionsPlugin';
import { ChatPlugins } from '@common/react/components/Chat/Chat';
import Application, { AfterLogout } from '@common/react/components/Core/Application/Application';
import NotificationGliderProvider from '@common/react/components/Chat/NotificationGliderProvider';
import Avatar from '@common/react/components/Forms/Avatar';
import MessageFlashingTime from '@common/react/components/Chat/MessageFlashingTime';
import { setLocalStorageValue } from '@common/react/utils/localStorage/localStorage';
import IdleTimer from '@common/react/components/UI/IdleTimer/IdleTimer';
import AntdModalContextProvider from '@common/react/components/Modal/AntdModalContextProvider';
import BrowserNotificationChecker from '@common/react/components/UI/BrowserNotificationChecker/BrowserNotificationChecker';
import GoogleKeysProvider from '@common/react/components/google/GoogleKeysProvider/GoogleKeysProvider';

import { LogoLoaderPage } from '@commonTuna/react/components/UI/Loaders/LogoLoader';

import CartProviderWithUpdate from '@app/components/UI/CartProviderWithUpdate/CartProviderWithUpdate';
import HeaderSearch from '@app/components/UI/Header/HeaderSearch';
import { MenuStateProvider } from '@app/components/UI/Menu/MenuStateProvider';
import { theme } from '@app/components/UI/ThemeConfig/ThemeConfig';
import PatientLocationsSetter from '@app/components/UI/PatientLocationsSetter/PatientLocationsSetter';
import { customReduxActions } from '@app/store/CustomReduxActions';
import { User, UserRole } from '@app/objects/User';
import { Init } from '@app/objects/Init';
import RouteWithFooter from '@app/components/Routes/RouteWithFooter';
import { ApplicationState } from '@app/store';
import { STORAGE_KEYS } from '@app/objects/StorageKeys';

import '@app/scss/style.scss';
import '@app/scss/components/chatMessageNotificationGlider.scss';

const params = { fallback: <></> };

const ReactionNotificationHandler = loadable(() =>
	import(/* webpackChunkName: "ReactionNotificationHandler" */ '@common/react/components/Chat/ReactionNotificationHandler'), params);

const SignalRChats = loadable(() =>
	import(/* webpackChunkName: "SignalRChats" */ '@common/react/components/Chat/SignalRChats'), params);

const VideoChatModal = loadable(() =>
	import(/* webpackChunkName: "VideoChatModal" */ '@common/react/components/UI/VideoChat/VideoChatModal'), params);

const VideoChatGlider = loadable(() =>
	import(/* webpackChunkName: "VideoChatGlider" */ '@common/react/components/Chat/VideoChatGlider'), params);

const NotFound = loadable(() =>
	import(/* webpackChunkName: "PageNotFound" */ '@common/react/components/UI/PageNotFound/PageNotFound'));

const renderAvatar = (state) => <Avatar
	type=""
	readonly
	className="avatar-container"
	thumbClassName=""
	showDownload={false}
	showOpenIcon={false}
	withModal={false}
	object={state}
	firstName={state.firstName ?? 'N'}
	lastName={state.lastName ?? 'N'}
/>;

const Layout: React.FC = ({ children }) => {
	const user = useSelector((state: ApplicationState) => state.login.user, shallowEqual);

	React.useEffect(() => {
		if (!user) {
			setLocalStorageValue('skipFillInvites', false);
		}
	}, [user]);

	return (
		<div className="app-container schema-gray-orange">
			<ErrorBoundaryWithSentry>
				<AntdModalContextProvider>
					<GoogleKeysProvider
						analyticsId="G-B67V40VWKF"
						mapKey="AIzaSyB_Vis358nPu31ZjN8OUdxbMwr9H9JgSso"
					>
						<LoaderProvider
							loader={<LogoLoaderPage />}
							other={{
								chunkLoader: <LogoLoaderPage />,
							}}
						>
							<RequestProvider
								errorComponents={{
									notFound: () => <RouteWithFooter component={NotFound} />,
								}}
							>
								<CartProviderWithUpdate>
									<MenuStateProvider>
										<ConfigProvider theme={theme}>
											<Application<User, Init>
												initCustomHandler={customReduxActions}
												checkUserAccess={(user: User, roles: Array<UserRole>, props?: any) =>
													user && roles.includes(user.role)}
											>
												<AfterLogout
													callback={() => localStorage.removeItem(STORAGE_KEYS.CHECKOUT_UID)}
												/>
												<HeaderSearch />
												<PatientLocationsSetter />
												<NotificationGliderProvider>
													<ChatSettingsProvider
														plugins={{
															// ChatPlugins.Reactions is not currently used. for this you need to add messageActions
															// [ChatPlugins.Reactions]: ReactionsPlugin
														}}
														// messageActions={[ ChatPlugins.Reactions ]}
													>
														{children || <Outlet />}
														{user ? (<>
															<MessageFlashingTime />
															<ReactionNotificationHandler />
															<SignalRChats />
															<IdleTimer />
															<BrowserNotificationChecker />
														</>) : null}
													</ChatSettingsProvider>
													{user ? (<>
														<VideoChatGlider renderAvatar={renderAvatar} />
														<VideoChatModal />
													</>) : null}
												</NotificationGliderProvider>
											</Application>
										</ConfigProvider>
									</MenuStateProvider>
								</CartProviderWithUpdate>
							</RequestProvider>
						</LoaderProvider>
					</GoogleKeysProvider>
				</AntdModalContextProvider>
			</ErrorBoundaryWithSentry>
		</div>
	);
};

export default Layout;
